import type { UserType } from "@/interfaces";
import { userService } from "@/services/userService";
import { useGetVendorsByCompanyId } from "@/services/vendorService";
import vendorUserService, { CreateVendorUserRequest } from "@/services/vendorUserService";
import { useUserStore } from "@/stores/userStore";
import { Button, Form, Input, message, Modal, Select, Spin } from "antd";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * CreateVendorUserModal component for creating vendor users with email lookup
 *
 * Features:
 * - When email is entered, checks if user already exists
 * - If user exists, auto-populates fields and makes them read-only
 * - Creates additional mapping for existing users
 * - Vendor selection is mandatory
 * - Automatically includes current company in the mapping
 */
export default function CreateVendorUserModal({ open, onCancel, onSuccess }: Props) {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [existingUser, setExistingUser] = useState<UserType | null>(null);

  const currentCompany = useUserStore((state) => state.currentCompany);
  const companyId = currentCompany?.id;

  // Use React Query hook to fetch vendors
  const { data: vendorsData, isLoading: isLoadingVendors } = useGetVendorsByCompanyId(companyId || null);
  const vendors = vendorsData?.data || [];

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setExistingUser(null);
    }
  }, [open, form]);

  const resetMappedUserFields = () => {
    setExistingUser(null);
    form.setFieldsValue({
      firstName: undefined,
      lastName: undefined,
      phone: undefined,
      designation: undefined,
      linkedinId: undefined,
    });
  };

  const handleEmailChange = async (email: string) => {
    if (!email || !email.includes("@")) {
      resetMappedUserFields();
      return;
    }

    setIsCheckingEmail(true);
    try {
      const response = await userService.getUserByEmail(email);
      if (response.data) {
        const foundUser = response.data as UserType;

        if (foundUser.role !== "User") {
          message.error("User is already mapped with another role and cannot be used as vendor.");
          resetMappedUserFields();
          return;
        }

        // User exists - populate form fields and make them read-only
        setExistingUser(foundUser);
        form.setFieldsValue({
          firstName: foundUser.firstName,
          lastName: foundUser.lastName,
          phone: foundUser.phone || "",
          designation: foundUser.designation || "",
          linkedinId: foundUser.linkedinId || "",
        });
        message.info("User found - creating additional mapping");
      } else {
        // User doesn't exist
        resetMappedUserFields();
      }
    } catch (error: any) {
      console.error("Error checking email:", error);
      resetMappedUserFields();
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailBlur = () => {
    const email = form.getFieldValue("email");
    if (email) {
      handleEmailChange(email);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setExistingUser(null);
    onCancel();
  };

  const handleFormSubmit = async (values: CreateVendorUserRequest & { vendorId: string }) => {
    if (!companyId) {
      message.error("No company selected");
      return;
    }

    setIsSaving(true);
    try {
      const payload: CreateVendorUserRequest = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || null,
        designation: values.designation || null,
        linkedinId: values.linkedinId || null,
        isPrimary: false,
        isActive: true,
        companyId: companyId,
      };

      // If user exists, include userId to create additional mapping
      if (existingUser) {
        payload.userId = existingUser.id;
      }

      const response = await vendorUserService.create(values.vendorId, payload);
      message.success(response.message || "Vendor user created successfully");
      onSuccess();
      handleCancel();
    } catch (error: any) {
      console.error("Error creating vendor user:", error);
      message.error(error?.response?.data?.message || error?.message || "Failed to create vendor user");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={
        <div className="mb-4">
          <span style={{ fontSize: 24, fontWeight: 700 }}>
            {existingUser ? "Map Existing User to Vendor" : "Create New Vendor User"}
          </span>
        </div>
      }
      centered
      open={open}
      onCancel={handleCancel}
      width={600}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Form.Item name="vendorId" label="Vendor" rules={[{ required: true, message: "Please select a vendor" }]}>
          <Select
            showSearch
            placeholder="Select vendor"
            loading={isLoadingVendors}
            optionFilterProp="children"
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            options={vendors.map((vendor) => ({
              label: vendor.vendorName,
              value: vendor.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            placeholder="user@example.com"
            onBlur={handleEmailBlur}
            suffix={isCheckingEmail ? <Spin size="small" /> : null}
          />
        </Form.Item>

        <Form.Item name="phone" label="Phone (Optional)">
          <Input placeholder="+1234567890" disabled={!!existingUser} readOnly={!!existingUser} />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="John" disabled={!!existingUser} readOnly={!!existingUser} />
          </Form.Item>

          <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Please enter last name" }]}>
            <Input placeholder="Doe" disabled={!!existingUser} readOnly={!!existingUser} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="designation" label="Designation (Optional)">
            <Input placeholder="Manager" disabled={!!existingUser} readOnly={!!existingUser} />
          </Form.Item>

          <Form.Item name="linkedinId" label="LinkedIn URL (Optional)">
            <Input placeholder="https://linkedin.com/in/username" disabled={!!existingUser} readOnly={!!existingUser} />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isSaving}>
            {existingUser ? "Map User" : "Create User"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
